import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ellipsis } from "polished";
import * as React from "react";
import { useCallback } from "react";
import { Link } from "react-router-dom";

import {
  ChatBotColored,
  CodeBlack,
  KnowledgeBaseColored,
  LiveChatColored,
  RosetteDiscountCheck,
  TextLogo as TextLogoIcon,
} from "@livechat/design-system-icons";
import {
  Avatar,
  DesignToken,
  Icon,
  IconSource,
  RadiusToken,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import logoTextBlack from "assets/img/text/text-logo-black.png";
import logoTextWhite from "assets/img/text/text-logo-white.png";
import { Divider } from "components/divider/Divider";
import { ExternalLink } from "components/external-link";
import { IconsEnum, Icon as InternalIcon } from "components/icon";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { config } from "config/config";
import { HelpCenter } from "constants/help-center";
import { Product } from "constants/product";
import { EventPlace, trackEngagement } from "helpers/analytics";
import useNewFeaturesInformation from "hooks/settings/use-new-features-information";
import useProductFeature from "hooks/use-product-feature";
import { IAgent } from "store/entities/agents";
import { Theme, withVar } from "themes/helpers";

import { NavigationAutoassignmentSwitcher } from "./NavigationAutoassignmentSwitcher";
import { NavigationThemeSwitcher } from "./NavigationThemeSwitcher";
import { menuItemButtonStyles, menuItemExternalLinkStyles } from "./styles";

interface IProps {
  agent: IAgent;
  currentTheme: Theme;
  productSwitcherEnabled: boolean;
  onHideMenu(): void;
  onLogout(): void;
  openChatWidget(): void;
  toggleShortcutsModalDisplayed(): void;
}

const logoTextStyle = css`
  margin-left: ${withVar(SpacingToken.Spacing1)};
  margin-right: ${withVar(SpacingToken.Spacing1)};
  margin-top: 0px;
  margin: 0 var(${SpacingToken.Spacing1});
`;

const StyledMenuWrapper = styled.div`
  width: 274px;
`;

const StyledProductsMenu = styled.ul`
  padding: 6px 0;
  border-radius: 4px;
  background-color: ${withVar(DesignToken.SurfaceSecondaryDefault)};

  h4 {
    display: flex;
    margin: 0;
    padding: 8px 16px;
    font-size: 14px;
    color: ${withVar(DesignToken.ContentBasicSecondary)};
    font-weight: 400;
  }
`;

const StyledProductMenuItem = styled.li<{ isTextAccounts?: boolean }>`
  list-style: none;

  a {
    position: relative;
    padding: 8px 16px;
    display: flex;
    font-size: 14px;
    cursor: pointer;
    text-decoration: none;

    &:hover {
      background-color: ${withVar(DesignToken.SurfaceSecondaryHover)};
    }
  }

  span {
    &.addition {
      display: flex;
      position: absolute;
      top: 50%;
      right: 16px;
      justify-content: center;
      min-width: 18px;
      transform: translateY(-50%);
    }
  }
`;

const StyledProductMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  overflow: hidden;
  margin-left: 15px;
  max-width: 100%;
  h3,
  span {
    margin: 0;
    color: ${withVar(DesignToken.ContentBasicSecondary)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  h3 {
    font-size: 14px;
    font-weight: 600;
    line-height: 18px;
    color: ${withVar(DesignToken.ContentBasicPrimary)};
  }
  span {
    font-size: 12px;
    line-height: 15px;
  }
`;

const NewBadge = styled.div`
  color: ${withVar(DesignToken.ContentBasicNegative)};
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`;

const StyledMenuProfile = styled(Link)`
  display: flex;
  margin: 6px 0;
  padding: 10px 16px;
  text-decoration: none;
  box-sizing: border-box;

  &:hover {
    background-color: ${withVar(DesignToken.SurfaceSecondaryHover)};
    text-decoration: none;
  }

  img {
    display: block;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    flex: 0 0 40px;
  }
`;

const StyledProductIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  border-radius: ${withVar(RadiusToken.Radius3)};
  background-color: ${withVar(DesignToken.SurfaceTertiaryDefault)};
`;

const StyledMenuProfileMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: 14px;
  padding: 3px 0;
  overflow: hidden;
  max-width: 100%;

  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 17px;
    color: ${withVar(DesignToken.ContentBasicPrimary)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    font-size: 12px;
    line-height: 15px;
    color: ${withVar(DesignToken.ContentBasicSecondary)};
    ${ellipsis()}
  }
`;

const StyledMenuList = styled.ul`
  margin: 0;
`;

const LogoutButton = css`
  position: relative;
  text-align: left;
  padding: 10px 16px;
  text-decoration: none;
  border: none;
  background-color: transparent;
  width: 100%;
  &:hover {
    background-color: ${withVar(DesignToken.SurfaceSecondaryHover)};
    border-radius: 0 0 var(${RadiusToken.Radius3}) var(${RadiusToken.Radius3});
  }
`;

function MenuItemButton({
  onClick,
  children,
}: {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button onClick={onClick} css={menuItemButtonStyles} type="button">
      <Text size="sm" customColor={withVar(DesignToken.ContentBasicPrimary)}>
        {children}
      </Text>
    </button>
  );
}

function MenuItemExternalLink({
  href,
  children,
  onClick,
}: {
  href: string;
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="nofollow noreferrer noopener"
      css={menuItemExternalLinkStyles}
      onClick={onClick}
    >
      <Text size="sm" customColor={withVar(DesignToken.ContentBasicPrimary)}>
        {children}
      </Text>
    </a>
  );
}

const StyledMenuListItem = styled.li`
  list-style: none;

  span {
    &.addition,
    &.hover-addition {
      position: absolute;
      left: 16px;
      top: 50%;
      justify-content: center;
      min-width: 18px;
      transform: translateY(-50%);
    }

    &.addition {
      display: flex;
    }

    &.hover-addition {
      display: none;
    }
  }
`;

type MyProfileProduct = Exclude<Product, Product.TextApp>;

const ProductNames: Record<MyProfileProduct, string> = {
  [Product.LiveChat]: "LiveChat",
  [Product.ChatBot]: "ChatBot",
  [Product.KnowledgeBase]: "KnowledgeBase",
  [Product.DeveloperProgram]: "Developer Program",
  [Product.PartnerProgram]: "Partner Program",
  [Product.TextAccounts]: "Text Accounts",
};

const ProductIcons: Record<MyProfileProduct, IconsEnum | IconSource> = {
  [Product.LiveChat]: LiveChatColored,
  [Product.ChatBot]: ChatBotColored,
  [Product.KnowledgeBase]: KnowledgeBaseColored,
  [Product.DeveloperProgram]: CodeBlack,
  [Product.PartnerProgram]: RosetteDiscountCheck,
  [Product.TextAccounts]: TextLogoIcon,
};

const ProductLinks: Record<MyProfileProduct, string> = {
  [Product.LiveChat]: config.livechatProductLink,
  [Product.ChatBot]: config.chatbotProductLink,
  [Product.KnowledgeBase]: config.knowledgebaseProductLink,
  [Product.DeveloperProgram]: config.devConsoleCreateAppUrl,
  [Product.PartnerProgram]: config.partnerProgramUrl,
  [Product.TextAccounts]: config.textAccountsPanel,
};

const ProductDescriptions: Record<MyProfileProduct, string> = {
  [Product.LiveChat]: "Connect with customers",
  [Product.ChatBot]: "Automate customer service with AI",
  [Product.KnowledgeBase]: "Guide and educate",
  [Product.DeveloperProgram]: "Build and monetize apps",
  [Product.PartnerProgram]: "Perfect for solution providers",
  [Product.TextAccounts]: "Manage your account and products",
};

const ShortcutsMenuButton: React.FC<{ onHideMenu(): void; toggleShortcutsModal(): void }> = ({
  onHideMenu,
  toggleShortcutsModal,
}) => {
  const handleLabelClick = (): void => {
    onHideMenu();
    toggleShortcutsModal();
  };

  return (
    <Flex
      padding="10px 16px"
      alignItems="center"
      gap="8px"
      onClick={handleLabelClick}
      css={{
        cursor: "pointer",
        ":hover": {
          backgroundColor: `var(${DesignToken.SurfacePrimaryHover})`,
        },
      }}
    >
      <Text size="sm" customColor={withVar(DesignToken.ContentBasicPrimary)}>
        Keyboard shortcuts
      </Text>
    </Flex>
  );
};

const ProductLink: React.FC<{ onHideMenu(): void; product: MyProfileProduct }> = ({ onHideMenu, product }) => {
  const hasNewFeature = useNewFeaturesInformation(product);
  const updateNewFeatureFlag = useProductFeature(product);

  const name = ProductNames[product];
  const description = ProductDescriptions[product];
  const icon = ProductIcons[product];
  const href = ProductLinks[product];

  const updateFeatureFlag = useCallback(() => {
    if (hasNewFeature) {
      updateNewFeatureFlag();
    }
  }, [hasNewFeature, updateNewFeatureFlag]);

  const handleProductClick = useCallback(
    (clickedProduct: Product) => {
      trackEngagement("Profile menu: Other product link clicked", EventPlace.ProfileMenu, {
        clickedProduct,
      });

      updateFeatureFlag();
      onHideMenu();
    },
    [onHideMenu, updateFeatureFlag]
  );

  return (
    <StyledProductMenuItem isTextAccounts={product === Product.TextAccounts}>
      <ExternalLink
        href={href}
        onClick={() => handleProductClick(product)}
        data-testid={`navmenu-product-${product.toLowerCase()}`}
      >
        <Flex alignItems="center">
          <StyledProductIconWrapper>
            {typeof icon === "string" ? (
              <InternalIcon
                fill={withVar(DesignToken.IllustrationsPrimary)}
                stroke={withVar(DesignToken.IllustrationsStroke)}
                name={icon}
                width="24px"
                height="24px"
              />
            ) : (
              <Icon source={icon} customColor={withVar(DesignToken.ContentBasicPrimary)} />
            )}
          </StyledProductIconWrapper>
          <StyledProductMeta>
            <Flex alignItems="center" gap="6px">
              <Text size="sm" bold customColor={withVar(DesignToken.ContentBasicPrimary)}>
                {name}
              </Text>
              {hasNewFeature && <NewBadge>NEW!</NewBadge>}
            </Flex>
            <Text size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)}>
              {description}
            </Text>
          </StyledProductMeta>
        </Flex>
      </ExternalLink>
    </StyledProductMenuItem>
  );
};

class NavigationMyProfileMenu extends React.PureComponent<IProps> {
  handleProductClick = (product: Product): void => {
    trackEngagement("Profile menu: Other product link clicked", EventPlace.ProfileMenu, {
      product,
    });
    this.props.onHideMenu();
  };

  handleProfileClick = (): void => {
    trackEngagement("Navbar: Agent details section opened", EventPlace.Navbar);
    this.props.onHideMenu();
  };

  handleLogoutClick = (): void => {
    this.props.onLogout();
    this.props.onHideMenu();
  };

  handleChatWithUsClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
    event.preventDefault();
    trackEngagement("Sidebar: Chat with us Help option clicked");

    this.props.openChatWidget();
    this.props.onHideMenu();
  };

  handleHandbookClick = (): void => {
    trackEngagement("Sidebar: Handbook Help option clicked");
    this.props.onHideMenu();
  };

  handleHelpCenterClick = (): void => {
    trackEngagement("Sidebar: Help Center Help option clicked");
    this.props.onHideMenu();
  };

  render(): JSX.Element {
    const { agent, currentTheme, productSwitcherEnabled } = this.props;
    const logoText = currentTheme === "dark" ? logoTextWhite : logoTextBlack;

    return (
      <StyledMenuWrapper>
        {!productSwitcherEnabled && (
          <>
            <StyledProductsMenu>
              <Flex padding="8px 16px" inline alignItems="center">
                <Text size="sm" customColor={withVar(DesignToken.ContentBasicPrimary)}>
                  Discover
                </Text>
                <img src={logoText} width="32" alt="Logo of Text, Inc." css={logoTextStyle} />
                <Text size="sm" customColor={withVar(DesignToken.ContentBasicPrimary)}>
                  products:
                </Text>
              </Flex>
              <ProductLink product={Product.LiveChat} onHideMenu={this.props.onHideMenu} />
              <ProductLink product={Product.ChatBot} onHideMenu={this.props.onHideMenu} />
              <ProductLink product={Product.KnowledgeBase} onHideMenu={this.props.onHideMenu} />
            </StyledProductsMenu>
            <Divider />
            <StyledProductsMenu>
              <Text
                size="sm"
                customColor={withVar(DesignToken.ContentBasicSecondary)}
                css={{ marginLeft: withVar(SpacingToken.Spacing4) }}
              >
                Partnership
              </Text>
              <ProductLink product={Product.DeveloperProgram} onHideMenu={this.props.onHideMenu} />
              <ProductLink product={Product.PartnerProgram} onHideMenu={this.props.onHideMenu} />
            </StyledProductsMenu>
            <Divider />
            <Block marginTop="6px" marginBottom="9px">
              <ProductLink product={Product.TextAccounts} onHideMenu={this.props.onHideMenu} />
            </Block>
            <Divider />
          </>
        )}
        <StyledMenuProfile
          to={`/agents/${agent.id}/edit`}
          onClick={this.handleProfileClick}
          data-testid="navmenu-profile"
        >
          <Avatar
            type={agent.avatar ? "image" : "text"}
            size="large"
            src={agent.avatar ? agent.avatar : undefined}
            text={agent.name}
          />
          <StyledMenuProfileMeta>
            <Text size="sm" bold customColor={withVar(DesignToken.ContentBasicPrimary)}>
              {agent.name}
            </Text>
            <Text size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)}>
              {agent.email}
            </Text>
          </StyledMenuProfileMeta>
        </StyledMenuProfile>
        <Divider />
        <StyledProductsMenu>
          <Text
            size="sm"
            customColor={withVar(DesignToken.ContentBasicSecondary)}
            css={{ marginLeft: withVar(SpacingToken.Spacing4) }}
          >
            Partnership
          </Text>
          <ProductLink product={Product.PartnerProgram} onHideMenu={this.props.onHideMenu} />
          <ProductLink product={Product.DeveloperProgram} onHideMenu={this.props.onHideMenu} />
        </StyledProductsMenu>
        <Divider />
        <NavigationAutoassignmentSwitcher hideProfileMenu={this.props.onHideMenu} />
        <NavigationThemeSwitcher />
        <Divider />
        <StyledMenuList>
          <StyledMenuListItem>
            <MenuItemExternalLink href="https://www.helpdesk.com/helpdesk-handbook/" onClick={this.handleHandbookClick}>
              HelpDesk Handbook
            </MenuItemExternalLink>
          </StyledMenuListItem>
          <StyledMenuListItem>
            <MenuItemExternalLink href={HelpCenter.HOME} onClick={this.handleHelpCenterClick}>
              Help Center
            </MenuItemExternalLink>
          </StyledMenuListItem>
          <StyledMenuListItem>
            <MenuItemButton onClick={this.handleChatWithUsClick}>Chat with us</MenuItemButton>
          </StyledMenuListItem>
        </StyledMenuList>
        <StyledMenuList>
          <StyledMenuListItem>
            <ShortcutsMenuButton
              onHideMenu={this.props.onHideMenu}
              toggleShortcutsModal={this.props.toggleShortcutsModalDisplayed}
            />
          </StyledMenuListItem>
          <Divider />
          <StyledMenuListItem>
            <button css={LogoutButton} onClick={this.handleLogoutClick} type="button" data-testid="navmenu-logout">
              <Text size="sm" customColor={withVar(DesignToken.ContentBasicPrimary)}>
                Log out
              </Text>
            </button>
          </StyledMenuListItem>
        </StyledMenuList>
      </StyledMenuWrapper>
    );
  }
}

export default NavigationMyProfileMenu;
